import { useAuthStore } from '@/modules/auth'

/** Objeto con lista de claves de permiso (p. ej. `TUser` del auth). */
export type TPermissionsSubject = {
  permissions?: string[]
}

export type HasPermissionOptions = {
  permission: string | string[]
  /**
   * Si no se envía, se usa el usuario de la sesión (`useAuthStore().user`).
   * Útil para tests o comparar otro sujeto sin tocar el store.
   */
  user?: TPermissionsSubject | null | undefined
  /** Solo se soporta `permissions` (clave serializada del API auth). */
  permissions_key?: 'permissions'
  /** Con `string[]`, `any` = OR, `all` = AND. Por defecto `any`. */
  mode?: 'any' | 'all'
}

/**
 * Evalúa si el sujeto tiene el/los permiso(s) en la lista del API.
 * Alineado con `user.permissions` del `UserResource` de auth (incluye super_admin con todos los keys).
 *
 * Requiere Pinia activa si omites `user` (SPA normal tras `app.use(pinia)`).
 */
export function hasPermission(options: HasPermissionOptions): boolean {
  const {
    permission,
    user: explicitUser,
    permissions_key = 'permissions',
    mode = 'any',
  } = options

  const user = explicitUser ?? useAuthStore().user

  const list = (user?.[permissions_key] ?? []) as string[]
  const keys = (Array.isArray(permission) ? permission : [permission]).filter(
    (k) => typeof k === 'string' && k.length > 0,
  )
  if (keys.length === 0) {
    return true
  }

  if (mode === 'all') {
    return keys.every((k) => list.includes(k))
  }

  return keys.some((k) => list.includes(k))
}

/**
 * Estilo composable: mismo `hasPermission` (el `user` sigue siendo opcional).
 * Útil si prefieres importar solo desde el composable en `<script setup>`.
 */
export function usePermissions() {
  return { hasPermission }
}
